import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useHistory } from "react-router-dom";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";
import {
  useListPartSchemas,
  useDeletePartSchema,
  useBulkDeletePartSchemas,
  useApplySchemaToParts,
  type PartSchema,
} from "../services/PartSchemaService";
import PartSchemaFormModal from "../components/PartSchemaFormModal";
import PartSchemaUploadModal from "../components/PartSchemaUploadModal";

const PartSchemaEditorPage = () => {
  const history = useHistory();
  const { data: schemas, isLoading } = useListPartSchemas();
  const deleteSchema = useDeletePartSchema();
  const bulkDeleteSchemas = useBulkDeletePartSchemas();
  const applySchema = useApplySchemaToParts();

  const [formOpen, setFormOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<PartSchema | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<PartSchema | undefined>(
    undefined,
  );
  const [applyingId, setApplyingId] = useState<number | null>(null);
  // Anchor + target for the "Apply" mode menu (deterministic vs LLM-assisted).
  const [applyMenuAnchor, setApplyMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [applyMenuSchema, setApplyMenuSchema] = useState<PartSchema | null>(
    null,
  );
  // Latch the delete so a fast double-click can't fire the mutation twice in
  // the render tick before `deleteSchema.isLoading` flips (mirrors the
  // disable-while-pending pattern in PartDetailView). Resets on completion.
  const [deleteTriggered, setDeleteTriggered] = useState(false);

  // Multi-select for bulk delete.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const schemaById = new Map((schemas ?? []).map((s) => [s.id, s]));

  // Drop any selected ids that no longer exist (e.g. after a delete/refetch).
  useEffect(() => {
    if (!schemas) return;
    const live = new Set(schemas.map((s) => s.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [schemas]);

  const allSelected = useMemo(
    () =>
      !!schemas && schemas.length > 0 && selectedIds.size === schemas.length,
    [schemas, selectedIds],
  );
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.size === (schemas?.length ?? 0)
        ? new Set()
        : new Set((schemas ?? []).map((s) => s.id)),
    );
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    bulkDeleteSchemas.mutate([...selectedIds], {
      onSuccess: (res) => {
        if (res.error_count > 0) {
          const firstError = res.errors[0]?.error ?? "";
          NucToast({
            message: `Deleted ${res.deleted_count} schema(s); ${res.error_count} could not be deleted${
              firstError ? `: ${firstError}` : ""
            }`,
            type: "warning",
            hideProgressBar: false,
          });
        } else {
          NucToast({
            message: `Deleted ${res.deleted_count} schema(s).`,
            type: "success",
          });
        }
        setSelectedIds(new Set());
        setBulkDeleteOpen(false);
      },
      onError: (err) => {
        const detail = extractErrorDetail(err);
        console.error("Failed to bulk-delete schemas", err);
        NucToast({
          message: `Failed to delete schemas: ${detail}`,
          type: "error",
          hideProgressBar: false,
        });
        setBulkDeleteOpen(false);
      },
    });
  };

  const openCreate = () => {
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (schema: PartSchema) => {
    setEditing(schema);
    setFormOpen(true);
  };

  const handleApply = (schema: PartSchema, useLlm: boolean) => {
    setApplyMenuAnchor(null);
    if (applyingId !== null) return; // an apply is already in flight
    setApplyingId(schema.id);
    applySchema.mutate(
      { schemaId: schema.id, useLlm },
      {
        onSuccess: (res) => {
          NucToast({
            message: `Queued ${res.queued} part${
              res.queued === 1 ? "" : "s"
            } of "${schema.part_type}" for ${
              useLlm ? "LLM-assisted" : "deterministic"
            } canonicalization.`,
            type: "success",
          });
          setApplyingId(null);
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error("Failed to apply schema", err);
          NucToast({
            message: `Failed to apply schema: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
          setApplyingId(null);
        },
      },
    );
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget || deleteTriggered) return;
    setDeleteTriggered(true);
    deleteSchema.mutate(deleteTarget.id, {
      onSuccess: () => {
        NucToast({
          message: `Schema "${deleteTarget.part_type}" deleted.`,
          type: "success",
        });
        setDeleteTarget(undefined);
        setDeleteTriggered(false);
      },
      onError: (err) => {
        const detail = extractErrorDetail(err);
        console.error("Failed to delete schema", err);
        NucToast({
          message: `Failed to delete schema: ${detail}`,
          type: "error",
          hideProgressBar: false,
        });
        setDeleteTarget(undefined);
        setDeleteTriggered(false);
      },
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => history.push("/parts/catalog")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>
          Part Schemas
        </Typography>
        {selectedIds.size > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="parts-schema-bulk-delete-btn"
          >
            Delete Selected ({selectedIds.size})
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          data-testid="parts-schema-upload-btn"
        >
          Upload Schemas
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          data-testid="parts-schema-create-btn"
        >
          New Schema
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the canonical features for each part type. Applying a schema
        canonicalizes existing parts of that type so they can be filtered by
        these features.
      </Typography>

      {isLoading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : !schemas || schemas.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No schemas yet. Create one to start filtering parts by typed
            features.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="parts-schema-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    inputProps={{ "aria-label": "Select all schemas" }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Part Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Features
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schemas.map((schema) => (
                <TableRow
                  key={schema.id}
                  hover
                  selected={selectedIds.has(schema.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(schema.id)}
                      onChange={() => toggleOne(schema.id)}
                      inputProps={{
                        "aria-label": `Select schema ${schema.part_type}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {schema.part_type}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={schema.description ?? undefined}
                    >
                      {schema.description || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {schema.parent_fk
                      ? (() => {
                          const parent = schemaById.get(schema.parent_fk);
                          return (
                            <Tooltip
                              title={parent ? "Edit parent schema" : ""}
                              disableHoverListener={!parent}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                clickable={!!parent}
                                onClick={
                                  parent ? () => openEdit(parent) : undefined
                                }
                                label={
                                  parent?.part_type ?? `#${schema.parent_fk}`
                                }
                              />
                            </Tooltip>
                          );
                        })()
                      : "—"}
                  </TableCell>
                  <TableCell
                    align="center"
                    data-testid={`parts-schema-feature-count-${schema.id}`}
                  >
                    {schema.features?.length ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Apply to existing parts">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setApplyMenuSchema(schema);
                            setApplyMenuAnchor(e.currentTarget);
                          }}
                          disabled={applyingId !== null}
                          aria-label="Apply schema to existing parts"
                          aria-haspopup="menu"
                          aria-controls={
                            applyMenuSchema?.id === schema.id &&
                            !!applyMenuAnchor
                              ? "apply-schema-menu"
                              : undefined
                          }
                          aria-expanded={
                            applyMenuSchema?.id === schema.id &&
                            !!applyMenuAnchor
                          }
                        >
                          {applyingId === schema.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PublishIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(schema)}
                        aria-label="Edit schema"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(schema)}
                        aria-label="Delete schema"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PartSchemaFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        schema={editing}
        parentOptions={(schemas ?? []).filter((s) => s.id !== editing?.id)}
      />

      <PartSchemaUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      <Menu
        id="apply-schema-menu"
        anchorEl={applyMenuAnchor}
        open={!!applyMenuAnchor}
        onClose={() => {
          setApplyMenuAnchor(null);
          setApplyMenuSchema(null);
        }}
        MenuListProps={{ role: "menu" }}
      >
        <MenuItem
          onClick={() => applyMenuSchema && handleApply(applyMenuSchema, false)}
        >
          <ListItemIcon>
            <PublishIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Apply (deterministic)"
            secondary="Match keys via the schema's aliases only"
          />
        </MenuItem>
        <MenuItem
          onClick={() => applyMenuSchema && handleApply(applyMenuSchema, true)}
        >
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Apply with LLM translation"
            secondary="Let the LLM map unmatched keys, then apply"
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={!!deleteTarget}
        onClose={
          deleteSchema.isLoading || deleteTriggered
            ? undefined
            : () => setDeleteTarget(undefined)
        }
      >
        <DialogTitle>Delete this schema?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This deletes the <strong>{deleteTarget?.part_type}</strong> schema.
            Parts keep their features but will no longer be filterable by this
            schema until another is applied. Schemas with child schemas cannot
            be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(undefined)}
            disabled={deleteSchema.isLoading || deleteTriggered}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteSchema.isLoading || deleteTriggered}
            startIcon={
              deleteSchema.isLoading || deleteTriggered ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteOpen}
        onClose={
          bulkDeleteSchemas.isLoading
            ? undefined
            : () => setBulkDeleteOpen(false)
        }
      >
        <DialogTitle>Delete {selectedIds.size} schema(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the selected schemas. Parts keep their features
            but will no longer be filterable by these schemas until another is
            applied. Child schemas are deleted before their parents; a schema
            still referenced by a live child that isn't selected will be skipped
            and reported.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteOpen(false)}
            disabled={bulkDeleteSchemas.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkDeleteSchemas.isLoading}
            startIcon={
              bulkDeleteSchemas.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartSchemaEditorPage;
